<template>
  <div>
    <div class="row wrapper border-bottom white-bg page-heading">
      <div class="col-lg-10">
        <h2>{{ title }}</h2>
        <ol class="breadcrumb">
          <li>
            <router-link to="/">Home</router-link>
          </li>
          <li v-if="breadcrumbParentRoute && breadcrumbParentLabel">
            <router-link :to="breadcrumbParentRoute">
              {{ breadcrumbParentLabel }}
            </router-link>
          </li>
          <li v-if="crumbAccountPk">
            <router-link :to="`/allegro/accounts/${crumbAccountPk}/overview`">
              {{ crumbAccountName }}
            </router-link>
          </li>
          <li v-if="settingsCrumb && crumbAccountPk">
            <router-link :to="`/allegro/accounts/${crumbAccountPk}/settings`">
              Ustawienia
            </router-link>
          </li>
          <li class="active">
            <strong>{{ crumbLabel || title }}</strong>
          </li>
        </ol>
      </div>
    </div>

    <div class="wrapper wrapper-content animated fadeInRight">
      <div class="row">
        <div class="col-lg-12">
          <div class="ibox float-e-margins">
            <div class="ibox-title">
              <h5>{{ title }}</h5>
            </div>
            <div class="ibox-content">
              <div v-if="errorMessage" class="alert alert-danger">
                {{ errorMessage }}
              </div>

              <div class="form-group" v-for="field in fields" :key="field.key">
                <label class="control-label col-sm-2">
                  {{ field.label }}
                  <span v-if="field.required">*</span>
                </label>
                <div class="col-sm-10">
                  <textarea
                    v-if="field.type === 'textarea'"
                    class="form-control inputClass"
                    :rows="field.rows || 5"
                    :placeholder="field.placeholder || ''"
                    :value="getFieldValue(field.key)"
                    @input="setFieldValue(field.key, $event.target.value)"
                  ></textarea>
                  <template v-else-if="field.type === 'file'">
                    <div v-if="getSavedFileUrl(field.key)" class="m-b-sm">
                      <a :href="getSavedFileUrl(field.key)" target="_blank">
                        <img
                          :src="getSavedFileUrl(field.key)"
                          :alt="field.label"
                          class="saved-file-preview"
                        />
                      </a>
                    </div>
                    <input
                      type="file"
                      class="form-control inputClass"
                      @change="setFile(field.key, $event)"
                    />
                  </template>
                  <input
                    v-else
                    :type="field.type || 'text'"
                    class="form-control inputClass"
                    :value="getFieldValue(field.key)"
                    @input="setFieldValue(field.key, $event.target.value)"
                    :placeholder="field.placeholder || ''"
                  />
                  <div v-if="getError(field.key)" class="has-error">
                    <span class="help-block">{{ getError(field.key) }}</span>
                  </div>
                </div>
              </div>

              <button class="btn btn-white" @click="goBack">Anuluj</button>
              <button
                v-if="canSubmit"
                class="btn btn-primary"
                @click="submitForm"
              >
                Zapisz
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import router from "../../router/router";
import { hasPermission } from "../../permissions";

export default {
  name: "AllegroJsonForm",
  props: {
    title: { type: String, required: true },
    breadcrumbParentRoute: { type: String, default: "/allegro/accounts/list" },
    breadcrumbParentLabel: { type: String, default: "Allegro - Moje konta" },
    // Short label of the last breadcrumb - the page title is usually prefixed
    // with "Allegro - ", which the breadcrumb does not repeat.
    crumbLabel: { type: String, default: "" },
    // Adds the account crumb, with the account taken from the edited object.
    accountCrumb: { type: Boolean, default: false },
    // Adds the account and "Ustawienia" crumbs - for the forms reachable from
    // the account settings tab.
    settingsCrumb: { type: Boolean, default: false },
    submitAction: { type: String, required: true },
    submitGetter: { type: String, required: true },
    loadAction: { type: String, default: "" },
    loadGetter: { type: String, default: "" },
    isUpdate: { type: Boolean, default: false },
    backRoute: { type: String, default: "/allegro/accounts/list" },
    successRoute: { type: String, default: "" },
    accountPkParam: { type: String, default: "" },
    accountField: { type: String, default: "account" },
    fields: { type: Array, default: () => [] },
    emptyObjects: { type: Array, default: () => [] },
    // Permission required to save the form - hides the submit button and
    // blocks the submit itself.
    modelPermission: { type: Object, default: null },
  },
  setup(props) {
    const store = useStore();
    const route = useRoute();
    const errorMessage = ref("");
    const formObj = ref({});
    const errors = ref({});

    const canSubmit = computed(
      () => !props.modelPermission || hasPermission(props.modelPermission)
    );

    const submitData = computed(() => store.getters[props.submitGetter]);
    const loadData = computed(() =>
      props.loadGetter ? store.getters[props.loadGetter] : null
    );

    // --- Account breadcrumb ---
    const accountData = computed(() => store.getters.getAllegroAccountData);
    const fetchedAccountName = ref("");

    const crumbAccountPk = computed(() => {
      // The account form edits the account itself.
      if (props.accountCrumb) {
        return route.params.pk || null;
      }
      if (!props.settingsCrumb) {
        return null;
      }
      // Create forms are opened from the account, update forms know it only
      // once the edited object is loaded.
      if (props.accountPkParam && route.params[props.accountPkParam]) {
        return route.params[props.accountPkParam];
      }
      return formObj.value.account || null;
    });

    const crumbAccountName = computed(() => {
      if (props.accountCrumb) {
        return formObj.value.name || "-";
      }
      return fetchedAccountName.value || "-";
    });

    watch(
      () => crumbAccountPk.value,
      (pk) => {
        if (pk && props.settingsCrumb) {
          store.dispatch("getAllegroAccount", pk);
        }
      },
      { immediate: true }
    );

    watch(
      () => accountData.value,
      (newValue) => {
        if (newValue?.status === 200) {
          fetchedAccountName.value = newValue.data?.name || "";
        }
      },
      { deep: true }
    );
    // --- End: account breadcrumb ---

    const fileFieldKeys = computed(() =>
      props.fields
        .filter((field) => field.type === "file")
        .map((field) => field.key)
    );

    // A loaded object holds the saved file as its URL - shown as a preview,
    // never sent back.
    const getSavedFileUrl = (fieldKey) => {
      const value = formObj.value[fieldKey];
      return typeof value === "string" ? value : "";
    };

    const setFile = (fieldKey, event) => {
      formObj.value[fieldKey] = event.target.files?.[0] || null;
    };

    const goBack = () => {
      if (route.params.pk && props.accountPkParam) {
        router.push(getBackRoute());
        return;
      }
      if (formObj.value.account) {
        router.push(getBackRoute());
        return;
      }
      router.push(props.backRoute);
    };

    const getBackRoute = () => {
      if (route.params.pk && props.accountPkParam) {
        return `/allegro/accounts/${
          route.params[props.accountPkParam]
        }/settings`;
      }
      if (formObj.value.account) {
        return `/allegro/accounts/${formObj.value.account}/settings`;
      }
      return props.backRoute;
    };

    const showSuccessToaster = () => {
      store.dispatch("showToaster", {
        type: "success",
        content: "Dane zostały zapisane.",
      });
    };

    const submitForm = () => {
      if (!canSubmit.value) {
        return;
      }

      const parsed = { ...formObj.value };

      // The backend file field accepts only an uploaded file - a saved file
      // comes back as its URL, so it is left out unless a new file was picked.
      fileFieldKeys.value.forEach((key) => {
        if (!(parsed[key] instanceof File)) {
          delete parsed[key];
        }
      });

      props.emptyObjects.forEach((key) => {
        const obj = parsed[key];

        if (
          !obj ||
          Object.values(obj).every(
            (value) => value === "" || value === null || value === undefined
          )
        ) {
          parsed[key] = {};
        }
      });

      if (props.isUpdate) {
        parsed.pk = route.params.pk;
      }
      if (props.accountPkParam && route.params[props.accountPkParam]) {
        parsed[props.accountField] = Number(route.params[props.accountPkParam]);
      }
      store.dispatch(props.submitAction, parsed);
    };

    if (props.accountPkParam && route.params[props.accountPkParam]) {
      formObj.value[props.accountField] = Number(
        route.params[props.accountPkParam]
      );
    }

    if (props.isUpdate && props.loadAction) {
      store.dispatch(props.loadAction, route.params.pk);
    }

    watch(
      () => loadData.value,
      (newValue) => {
        if (!newValue || newValue.status !== 200) {
          return;
        }
        formObj.value = { ...newValue.data };
      },
      { deep: true }
    );

    watch(
      () => submitData.value,
      (newValue) => {
        if (!newValue) {
          return;
        }
        if (newValue.status >= 200 && newValue.status < 300) {
          errors.value = {};
          errorMessage.value = "";
          if (props.successRoute) {
            router
              .push(props.successRoute.replace(":pk", route.params.pk))
              .then(showSuccessToaster);
          } else {
            router.push(getBackRoute()).then(showSuccessToaster);
          }
        } else if (newValue.data) {
          errors.value = newValue.data;
          const content =
            getFirstError(newValue.data) || "Nie udało się zapisać danych.";
          errorMessage.value = content;
          store.dispatch("showToaster", {
            type: "error",
            content: content,
          });
        }
      },
      { deep: true }
    );

    const getFieldValue = (key) => {
      return key.split(".").reduce((obj, part) => obj?.[part], formObj.value);
    };

    const setFieldValue = (key, value) => {
      const parts = key.split(".");
      let current = formObj.value;

      parts.slice(0, -1).forEach((part) => {
        if (!current[part]) {
          current[part] = {};
        }
        current = current[part];
      });

      current[parts[parts.length - 1]] = value;
    };

    const getError = (key) => {
      const error = key
        .split(".")
        .reduce((obj, part) => obj?.[part], errors.value);

      return Array.isArray(error) ? error[0] : error;
    };

    const getFirstError = (obj, path = "") => {
      for (const [key, value] of Object.entries(obj)) {
        const currentPath = path ? `${path}.${key}` : key;

        if (Array.isArray(value)) {
          return `${currentPath}: ${value[0]}`;
        }

        if (value && typeof value === "object") {
          const nested = getFirstError(value, currentPath);
          if (nested) {
            return nested;
          }
        }
      }

      return null;
    };

    return {
      formObj,
      errors,
      errorMessage,
      canSubmit,
      submitForm,
      goBack,
      setFile,
      getSavedFileUrl,
      getFieldValue,
      setFieldValue,
      getError,
      crumbAccountPk,
      crumbAccountName,
    };
  },
};
</script>

<style scoped>
.saved-file-preview {
  max-width: 200px;
  max-height: 150px;
}
</style>
